import React, { useEffect, useState, useRef } from 'react';
import { safeArray, unwrapListResponse } from '../../components/ErrorBoundary';
import { useProjects } from '../../context/ProjectsContext';
import ProjectFilterSelect from '../../components/ProjectFilterSelect';
import ResetFiltersButton from '../../components/ResetFiltersButton';
import { printTableElement } from '../../utils/exportUtils';
import { scopeAPI } from '../../services/authService';
import { confirmDialog } from '../../utils/confirmDialog';
import { notifySuccess, notifyError } from '../../utils/notify';

const ClientOffice = () => {
  const { projects: projectOptions } = useProjects();
  const [dataReady, setDataReady] = useState(false);
  const [projectFilter, setProjectFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editOffice, setEditOffice] = useState(null);
  const [formError, setFormError] = useState('');
  const [clientOffices, setClientOffices] = useState([]);
  const [newOffice, setNewOffice] = useState({
    project_id: '',
    office_name: '',
    department: '',
    address: '',
    contact_person: '',
    contact_phone: '',
    contact_email: '',
    visiting_hours: '',
    key_officials: '',
    appointment_required: false,
  });
  const contentRef = useRef(null);

  useEffect(() => {
    const loadClientOffices = async () => {
      try {
        const response = await scopeAPI.getClientOffices();
        const { rows } = unwrapListResponse(response);
        const mapped = safeArray(rows).map((row) => ({
          id: row.id,
          project_id: row.projectId ?? row.project_id ?? 1,
          office_name: row.officeName ?? row.office_name ?? '',
          address: row.address || '',
          contact_person: row.contactPerson ?? row.contact_person ?? '',
          contact_phone: row.contactPhone ?? row.contact_phone ?? '',
          contact_email: row.contactEmail ?? row.contact_email ?? '',
          department: row.department || '',
          visiting_hours: row.visitingHours ?? row.visiting_hours ?? '',
          appointment_required: row.appointmentRequired ?? row.appointment_required ?? false,
          key_officials: row.keyOfficials ?? row.key_officials ?? '',
        }));
        setClientOffices(mapped);
      } catch (error) {
        console.warn('Client office API unavailable, using local data:', error?.message || error);
        setClientOffices([]);
      } finally {
        setDataReady(true);
      }
    };
    loadClientOffices();
  }, []);

  const projects = safeArray(projectOptions);

  const filteredOffices = projectFilter === 'all'
    ? clientOffices
    : clientOffices.filter(o => o.project_id === parseInt(projectFilter, 10));

  if (!dataReady) {
    return (
      <div>
        <div className="section-header"><h2>Client Office</h2></div>
        <p style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>Loading...</p>
      </div>
    );
  }

  const validatePhone = (v) => !v || /^\d{10}$/.test(String(v).replace(/\s/g, ''));

  const openAddModal = () => {
    setEditOffice(null);
    setFormError('');
    setNewOffice({
      project_id: '',
      office_name: '',
      department: '',
      address: '',
      contact_person: '',
      contact_phone: '',
      contact_email: '',
      visiting_hours: '',
      key_officials: '',
      appointment_required: false,
    });
    setShowModal(true);
  };

  const openEditModal = (office) => {
    setEditOffice(office);
    setFormError('');
    setNewOffice({
      project_id: String(office.project_id),
      office_name: office.office_name || '',
      department: office.department || '',
      address: office.address || '',
      contact_person: office.contact_person || '',
      contact_phone: office.contact_phone || '',
      contact_email: office.contact_email || '',
      visiting_hours: office.visiting_hours || '',
      key_officials: office.key_officials || '',
      appointment_required: !!office.appointment_required,
    });
    setShowModal(true);
  };

  const handleCreateClientOffice = async () => {
    if (!newOffice.project_id || !newOffice.office_name.trim() || !newOffice.address.trim()) {
      notifyError('Project, office name, and address are required.');
      return;
    }
    if (newOffice.contact_phone && !validatePhone(newOffice.contact_phone)) {
      notifyError('Phone must be 10 digits.');
      return;
    }
    if (!(await confirmDialog('Add this client office?', 'New Client Office'))) return;
    try {
      const response = await scopeAPI.createClientOffice({
        projectId: Number(newOffice.project_id),
        officeName: newOffice.office_name.trim(),
        address: newOffice.address.trim(),
        contactPerson: newOffice.contact_person.trim() || null,
        contactPhone: newOffice.contact_phone.trim() || null,
        contactEmail: newOffice.contact_email.trim() || null,
        department: newOffice.department.trim() || null,
        visitingHours: newOffice.visiting_hours.trim() || null,
        appointmentRequired: !!newOffice.appointment_required,
        keyOfficials: newOffice.key_officials.trim() || null,
      });
      const row = response?.data;
      setClientOffices((prev) => ([
        {
          id: row.id,
          project_id: row.projectId ?? Number(newOffice.project_id),
          office_name: row.officeName ?? newOffice.office_name,
          address: row.address ?? newOffice.address,
          contact_person: row.contactPerson ?? newOffice.contact_person,
          contact_phone: row.contactPhone ?? newOffice.contact_phone,
          contact_email: row.contactEmail ?? newOffice.contact_email,
          department: row.department ?? newOffice.department,
          visiting_hours: row.visitingHours ?? newOffice.visiting_hours,
          appointment_required: row.appointmentRequired ?? !!newOffice.appointment_required,
          key_officials: row.keyOfficials ?? newOffice.key_officials,
        },
        ...prev,
      ]));
      setShowModal(false);
      setNewOffice({
        project_id: '',
        office_name: '',
        department: '',
        address: '',
        contact_person: '',
        contact_phone: '',
        contact_email: '',
        visiting_hours: '',
        key_officials: '',
        appointment_required: false,
      });
      notifySuccess('Client office added successfully.');
    } catch (error) {
      console.error('Create client office failed via API:', error);
      setFormError('Unable to add client office.');
      notifyError('Unable to add client office. Please retry.');
    }
  };

  const handleUpdateClientOffice = async () => {
    setFormError('');
    if (!editOffice || !newOffice.project_id || !newOffice.office_name.trim() || !newOffice.address.trim()) {
      setFormError('Project, office name, and address are required.');
      return;
    }
    if (newOffice.contact_phone && !validatePhone(newOffice.contact_phone)) {
      setFormError('Phone must be 10 digits.');
      return;
    }
    if (!(await confirmDialog('Update this client office?', 'Update Client Office'))) return;
    try {
      const response = await scopeAPI.updateClientOffice(editOffice.id, {
        projectId: Number(newOffice.project_id),
        officeName: newOffice.office_name.trim(),
        address: newOffice.address.trim(),
        contactPerson: newOffice.contact_person.trim() || null,
        contactPhone: newOffice.contact_phone.trim() || null,
        contactEmail: newOffice.contact_email.trim() || null,
        department: newOffice.department.trim() || null,
        visitingHours: newOffice.visiting_hours.trim() || null,
        appointmentRequired: !!newOffice.appointment_required,
        keyOfficials: newOffice.key_officials.trim() || null,
      });
      const row = response?.data;
      setClientOffices((prev) => prev.map((o) => (o.id === editOffice.id ? {
        ...o,
        project_id: row.projectId ?? o.project_id,
        office_name: row.officeName ?? o.office_name,
        address: row.address ?? o.address,
        contact_person: row.contactPerson ?? o.contact_person,
        contact_phone: row.contactPhone ?? o.contact_phone,
        contact_email: row.contactEmail ?? o.contact_email,
        department: row.department ?? o.department,
        visiting_hours: row.visitingHours ?? o.visiting_hours,
        appointment_required: row.appointmentRequired ?? o.appointment_required,
        key_officials: row.keyOfficials ?? o.key_officials,
      } : o)));
      setShowModal(false);
      setEditOffice(null);
      notifySuccess('Client office updated successfully.');
    } catch (error) {
      console.error('Update client office failed:', error);
      setFormError('Unable to update client office.');
      notifyError('Unable to update client office. Please retry.');
    }
  };

  const handleDeleteClientOffice = async (office) => {
    if (!(await confirmDialog(`Delete "${office.office_name}"?`, 'Delete'))) return;
    try {
      await scopeAPI.deleteClientOffice(office.id);
      setClientOffices((prev) => prev.filter((o) => o.id !== office.id));
      notifySuccess('Client office deleted.');
    } catch (error) {
      console.error('Delete client office failed:', error);
      notifyError('Unable to delete client office. Please retry.');
    }
  };

  return (
    <div>
      <div className="section-header">
        <h2>Client Office <span className="count-badge">{filteredOffices.length}</span></h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn-secondary"
            onClick={() => printTableElement({ title: 'Client Office - Filtered', subtitle: `Project: ${projectFilter === 'all' ? 'All Projects' : (projects.find(p => p.id === parseInt(projectFilter, 10))?.name || 'Selected Project')}`, element: contentRef.current })}
          >
            Download PDF
          </button>
          <button className="btn-primary" onClick={openAddModal}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            Add Client Office
          </button>
        </div>
      </div>

      <div className="filter-bar">
        <ProjectFilterSelect
          value={projectFilter}
          onChange={setProjectFilter}
          projects={projects}
        />
        <ResetFiltersButton onClick={() => setProjectFilter('all')} />
      </div>

      <div
        ref={contentRef}
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 20 }}
      >
        {filteredOffices.map((office) => {
          const project = projects.find(p => p.id === office.project_id);
          return (
            <div
              key={office.id}
              style={{
                background: 'white',
                borderRadius: 8,
                border: '1px solid #e5e7eb',
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                transition: 'box-shadow 0.2s',
              }}
            >
              <div style={{ height: 4, background: '#7C3AED' }} />
              <div style={{ padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 8, background: '#F5F3FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 20, height: 20 }}>
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                        <polyline points="9 22 9 12 15 12 15 22" />
                      </svg>
                    </div>
                    <div>
                      <h4 style={{ fontSize: 15, fontWeight: 600, color: '#1f2937', margin: 0 }}>{office.office_name}</h4>
                      <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0 0' }}>{project?.name}</p>
                    </div>
                  </div>
                  {office.department && (
                    <span style={{ background: '#f3f4f6', color: '#4b5563', fontSize: 11, fontWeight: 500, padding: '4px 8px', borderRadius: 6 }}>{office.department}</span>
                  )}
                </div>

                <div style={{ padding: '10px 12px', background: '#f9fafb', borderRadius: 6, marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14, flexShrink: 0, marginTop: 2 }}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                    <span>{office.address}</span>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                  <div>
                    <p style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 2px 0' }}>CONTACT</p>
                    <p style={{ fontSize: 13, fontWeight: 500, color: '#1f2937', margin: 0 }}>{office.contact_person || '—'}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 2px 0' }}>PHONE</p>
                    <p style={{ fontSize: 13, fontWeight: 500, color: '#1f2937', margin: 0 }}>{office.contact_phone || '—'}</p>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <p style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 2px 0' }}>EMAIL</p>
                    <p style={{ fontSize: 13, fontWeight: 500, color: '#7C3AED', margin: 0 }}>{office.contact_email ? <a href={`mailto:${office.contact_email}`} style={{ color: 'inherit', textDecoration: 'none' }}>{office.contact_email}</a> : '—'}</p>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <p style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 2px 0' }}>VISITING HOURS</p>
                    <p style={{ fontSize: 13, color: '#374151', margin: 0, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {office.visiting_hours || '—'}
                      {office.appointment_required && (
                        <span style={{ padding: '2px 8px', background: '#fef3c7', color: '#b45309', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>Appointment Required</span>
                      )}
                    </p>
                  </div>
                </div>

                {office.key_officials && (
                  <div style={{ marginBottom: 12, paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
                    <p style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 8px 0' }}>KEY OFFICIALS</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {(office.key_officials || '').split(', ').filter(Boolean).map((official, i) => (
                        <span key={i} style={{ fontSize: 12, color: '#374151' }}>
                          {official.includes(': ') ? (
                            <><span style={{ color: '#6b7280' }}>{official.split(': ')[0]}:</span> {official.split(': ')[1]}</>
                          ) : official}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
                  <button className="btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); openEditModal(office); }}>Edit</button>
                  <button className="btn-secondary btn-sm" style={{ borderColor: '#fecaca', color: '#b91c1c' }} onClick={(e) => { e.stopPropagation(); handleDeleteClientOffice(office); }}>Delete</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editOffice ? 'Edit Client Office' : 'Add Client Office'}</h2>
              <button className="btn-close" onClick={() => setShowModal(false)}><svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg></button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group"><label>Project <span className="req">*</span></label><select value={newOffice.project_id} onChange={(e) => setNewOffice({ ...newOffice, project_id: e.target.value })}><option value="">Select</option>{projects.filter(p => p.status === 'Active').map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
                <div className="form-group"><label>Office Name <span className="req">*</span></label><input type="text" value={newOffice.office_name} onChange={(e) => setNewOffice({ ...newOffice, office_name: e.target.value })} placeholder="e.g. NHAI Regional Office" /></div>
              </div>
              <div className="form-group"><label>Department</label><input type="text" value={newOffice.department} onChange={(e) => setNewOffice({ ...newOffice, department: e.target.value })} placeholder="e.g. NHAI - PIU Pune" /></div>
              <div className="form-group"><label>Address <span className="req">*</span></label><textarea value={newOffice.address} onChange={(e) => setNewOffice({ ...newOffice, address: e.target.value })} placeholder="Full address..." rows={2} /></div>
              <div className="form-row">
                <div className="form-group"><label>Contact Person</label><input type="text" value={newOffice.contact_person} onChange={(e) => setNewOffice({ ...newOffice, contact_person: e.target.value })} /></div>
                <div className="form-group"><label>Phone</label><input type="tel" value={newOffice.contact_phone} onChange={(e) => setNewOffice({ ...newOffice, contact_phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} placeholder="10 digits" maxLength={10} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Email</label><input type="email" value={newOffice.contact_email} onChange={(e) => setNewOffice({ ...newOffice, contact_email: e.target.value })} /></div>
                <div className="form-group"><label>Visiting Hours</label><input type="text" value={newOffice.visiting_hours} onChange={(e) => setNewOffice({ ...newOffice, visiting_hours: e.target.value })} placeholder="e.g. Mon-Fri: 10AM-5PM" /></div>
              </div>
              <div className="form-group"><label>Key Officials</label><textarea value={newOffice.key_officials} onChange={(e) => setNewOffice({ ...newOffice, key_officials: e.target.value })} placeholder="SE: Name, EE: Name, AE: Name" rows={2} /></div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={newOffice.appointment_required} onChange={(e) => setNewOffice({ ...newOffice, appointment_required: e.target.checked })} />
                  Appointment Required
                </label>
              </div>
              {formError && <p style={{ color: '#dc2626', fontSize: 12 }}>{formError}</p>}
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                {editOffice ? (
                  <button className="btn-primary" onClick={handleUpdateClientOffice}>Update Office</button>
                ) : (
                  <button className="btn-primary" onClick={handleCreateClientOffice}>Add Office</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientOffice;
